import React, { useState } from "react";
import Eye from "../../images/svgs/Eye";
import Message from "../../images/icons/chat.png";
import { getAllMentorAppointments } from "../../services/dashboardService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import Modal from "../../components/modal/Modal";
import DropDown2 from "../../components/dropDown/DrobDown2";
import { Link } from "react-router-dom";
import { workYears } from "../../constants/constants";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { Trash2 } from "lucide-react";
import { api1 } from "../../utils/axiosInstance";
import Sidebar from "../../components/Sidebar";
import SecondaryLoader from "../../components/SecondaryLoader/SecondaryLoader";

const statusColors = {
  UPCOMING: "bg-pending text-white",
  COMPLETED: "bg-accept text-white",
  CANCELED: "bg-reject text-white",
};

const LabelValue = ({ label, value }) => (
  <p className="flex flex-col items-left text-[#212529]">
    <span className="font-semibold">{label}</span>
    <span className="font-bold">{value}</span>
  </p>
);

const BookingSummary = () => {
  const mentorTimezone = useSelector((state) => state.mentor.profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const userId = useSelector((state) => state.auth.id);
  const queryClient = useQueryClient();

  const timeConversion = (input) => {
    const [startUtc, endUtc] = input.split(" - ");
    const start = moment.utc(startUtc).tz(mentorTimezone);
    const end = moment.utc(endUtc).tz(mentorTimezone);

    return {
      date: start?.format("DD-MM-YYYY"),
      slot: `${start?.format("HH:mm")}-${end?.format("HH:mm")}`,
      end: end,
    };
  };

  const getDerivedStatus = (booking) => {
    const [, endUtc] = booking.selectedTime.split(" - ");
    const now = moment.utc();

    if (booking.status === 2) return "CANCELED";
    if (booking.status === 1) return "COMPLETED";
    if (booking.status === 0 && moment.utc(endUtc).isAfter(now)) return "UPCOMING";
    return "CANCELED";
  };

  const { data: appointmentsData, isLoading } = useQuery({
    queryKey: ["all_appointments", userId],
    queryFn: () => getAllMentorAppointments(userId),
    enabled: !!userId, 
  });

  const cancelAppointment = useMutation({
    mutationFn: async ({appointmentId, paymentId}) => {
      const response = await api1.post(`/api/appointment/cancel-appointment/${appointmentId}`, { paymentId: paymentId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["all_appointments", userId]);
    },
  });

  const handleCancel = (appointmentId, paymentId) => {
    toast.info(
      <div>
        <p>Are you sure you want to cancel this appointment?</p>
        <div className="flex gap-3 mt-2">
          <button
            onClick={() => {
              cancelAppointment.mutate({appointmentId, paymentId});
              toast.dismiss();
            }}
            className="px-3 py-1 bg-red-600 text-white rounded"
          >
            Yes, Cancel
          </button>
          <button
            onClick={() => toast.dismiss()}
            className="px-3 py-1 border rounded"
          >
            No
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        draggable: false,
        position: "top-center",
      }
    );
  };

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("ALL");

  const filteredAppointments = appointmentsData?.filter((booking) => {
    const derivedStatus = getDerivedStatus(booking);
    return filterStatus === "ALL" || filterStatus === derivedStatus;
  });

  const sortedAppointments = filteredAppointments?.sort((a, b) => {
    return new Date(b.date) - new Date(a.date);
  });

  const openModal = (appointment) => {
    setSelectedAppointment(appointment);
    setIsOpen(true);
  };

  return (
    <Sidebar>
      <div className="w-full min-h-screen">
        <DropDown2
          label={"Filter By Tag"}
          defaultValue={{ label: "Upcoming", value: "UPCOMING" }}
          options={[
            { label: "All", value: "ALL" },
            { label: "Upcoming", value: "UPCOMING" },
            { label: "Completed", value: "COMPLETED" },
          ]}
          onChange={(value) => setFilterStatus(value)}
          className="mb-5"
        />

      {isLoading ? (
        <SecondaryLoader message="Loading appointments..." />
      ) : (
        <>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        width="w-[700px]"
        label={"Appointment Details"}
      >
        {selectedAppointment && (
          <div className="mx-auto mb-5">
            <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2 border-custom-blue">
              <h3 className="text-lg font-semibold">MENTEE DETAILS</h3>
              <div className="border-b pt-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 text-gray-600">
                <LabelValue label={"NAME"} value={`${selectedAppointment?.menteeDetails?.firstName} ${selectedAppointment?.menteeDetails?.lastName}`} />
                <LabelValue label={"Mentee Current Job/Occupation"} value={`${selectedAppointment?.menteeDetails?.currentJob}`} />
                <LabelValue label={"Mentee Years of Work Experience"} value={`${workYears.find((skill) => skill.value === selectedAppointment?.menteeDetails?.yearsOfExperience)?.label}`} />
                <LabelValue label={"Mentee Selected Target Occupation"} value={`${selectedAppointment?.menteeDetails?.targetJob}`} />
                <LabelValue label={"Mentee Career Goals"} value={`${selectedAppointment?.menteeApplication?.careerGoals}`} />
                <LabelValue label={"Mentorship Goals"} value={`${selectedAppointment?.menteeApplication?.mentorshipGoals}`} />
                <LabelValue label={"Current Challenges"} value={`${selectedAppointment?.menteeApplication?.currentChallenges}`} />
              </div>
            </div>

            <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2 border-custom-blue">
              <h3 className="text-lg font-semibold">APPOINTMENT DETAILS</h3>
              <div className="border pt-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 text-gray-600">
                <LabelValue label={"DATE"} value={timeConversion(selectedAppointment.selectedTime).date} />
                <LabelValue label={"TIME SLOT"} value={timeConversion(selectedAppointment.selectedTime).slot} />
                <LabelValue label={"Duration"} value={`${selectedAppointment.meetingForTime} MINUTES`} />
                <LabelValue label={"STATUS"} value={getDerivedStatus(selectedAppointment)} />
              </div>
            </div>
          </div>
        )}
      </Modal>

      <div className="mx-auto bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-xl font-bold mb-4">Booking Summary</h2>
        <div className="w-full overflow-x-auto">
          {sortedAppointments && sortedAppointments.length > 0 ? (
            <table className="w-full border-collapse">
              <thead className="border border py-3">
                <tr className="py-3 uppercase text-sm">
                  <th className="py-3 text-left w-auto px-4">MENTEE NAME</th>
                  <th className="text-left w-auto px-4">APPOINTMENT DATE</th>
                  <th className="text-left w-auto px-4">APPOINTMENT SLOT</th>
                  <th className="text-center w-auto px-4">TAGS</th>
                  <th className="text-left w-auto px-4 pl-10">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {sortedAppointments.map((booking, index) => {
                  const status = getDerivedStatus(booking);
                  const showActions = status === "UPCOMING";
                  return (
                    <tr key={index} className="border">
                      <td className="py-3 px-4 font-medium text-nowrap">
                        {`${booking?.menteeDetails?.firstName} ${booking?.menteeDetails?.lastName}`}
                      </td>
                      <td className="px-4">{timeConversion(booking.selectedTime).date}</td>
                      <td className="px-4">{timeConversion(booking.selectedTime).slot}</td>
                      <td className="text-center px-4">
                        <span className={`px-3 py-1 rounded text-sm font-semibold ${statusColors[status]}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-4 pl-10">
                        <div className="flex gap-3 flex-wrap items-center">
                          <button
                            onClick={() => openModal(booking)}
                            className="text-[#1db9aa] flex items-center gap-1 bg-[#e1f6f6] p-1 px-2 rounded"
                          >
                            <Eye className="text-[#1db9aa] w-5" />
                          </button>

                          <Link to={`/dashboard/mentors-and-community/mentorship/chat/${booking.menteesId}`}>
                            <button className="text-blue-800 flex items-center gap-1 bg-blue-100 p-1 px-2 rounded">
                              <img src={Message} className="w-5" />
                              Chat with Mentee
                            </button>
                          </Link>

                          {showActions && (
                            <div className="flex gap-3">
                              <Link to={booking?.meetingLink}>
                                <button className="text-[#1D1E9D] flex items-center gap-1 bg-[#1D1E9D12] p-1 px-2 rounded">
                                  <div
                                    className="bg-green-600 rounded-full"
                                    style={{ width: "5.43px", height: "5.43px" }}
                                  ></div>
                                  Join Meeting
                                </button>
                              </Link>
                              <button
                                onClick={() => handleCancel(booking._id, booking.paymentId)}
                                className="text-red-600 flex items-center gap-1 bg-red-100 p-1 px-2 rounded"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Appointments Found</h3>
              <p className="text-gray-500 max-w-md">
                {filterStatus === "ALL" 
                  ? "You don't have any appointments yet. When mentees book sessions with you, they will appear here."
                  : `You don't have any ${filterStatus.toLowerCase()} appointments. Try changing the filter to see other appointments.`
                }
              </p>
            </div>
          )}
        </div>
      </div>
        </>
      )}
    </div>
    </Sidebar>
  );
};

export default BookingSummary;
